import ViewportModule from '../viewport/Module'

import { TReactRenderTarget } from '@aytm/lib/common/reactCreateRoot'

import { PREVIEW_TOOLBAR_CLASS_NAME } from '../preview/components/const'

import { ModuleID } from '../types'
import { IModal, IModalModule, IModalProps, ModalID } from './types'

import {
  MODAL_DEFAULT_PROPS,
  SWIDGET_MODAL_BACKGROUND_TRANSITION,
  SWIDGET_MODAL_CLASS_NAME,
  SWIDGET_MODAL_ID_PREFIX,
  SWIDGET_MODAL_IMAGE_BACKGROUND_CLASS_NAME,
  SWIDGET_MODAL_STYLE_ID_PREFIX,
  SWIDGET_MODAL_TARGET_CLASS_NAME,
  SWIDGET_MODAL_TARGET_ID_PREFIX
} from './const'

const modals = new Map<ModalID, { props: IModalProps; modal: IModal }>()

let moduleLoaded = false
const ModalModule: IModalModule = {
  get id() {
    return ModuleID.MODAL
  },
  config(props = {}) {
    for (const modalID of Object.keys(props)) {
      if (modals.has(modalID)) {
        const modal = modals.get(modalID)
        if (!modal.props.freezeStyle) {
          const modalProps = props[modalID] || {}
          const lastBackground = modal.props.background ? Object.assign({}, modal.props.background) : {}
          modal.props = {
            ...modal.props,
            ...modalProps,
            background: { ...lastBackground, ...(modalProps.background || {}) }
          }
          if (null != modal.props.active && modal.modal) {
            modal.modal.active = modal.props.active
          }
          if (
            (lastBackground.color !== modal.props.background?.color ||
              lastBackground.image !== modal.props.background?.image ||
              lastBackground.tile !== modal.props.background?.tile ||
              lastBackground.fit !== modal.props.background?.fit ||
              lastBackground.unhovered !== modal.props.background?.unhovered) &&
            modal.modal &&
            modal.modal?.containerElement
          ) {
            modal.modal.styleElement.innerHTML = buildStyle(modal.modal.containerElement.id, modal.props)
          }
        }
      } else {
        let modalContainer: HTMLDivElement = null
        let modalStyle: HTMLStyleElement = null
        let modalTarget: TReactRenderTarget = null
        let backupOverflow = ''
        const initProps: IModalProps = Object.assign({}, MODAL_DEFAULT_PROPS, { document: document }, props[modalID])
        const modal: IModal = {
          get active() {
            const modalProps = modals.get(modalID).props
            return modalProps.active
          },
          set active(active) {
            const modalProps = modals.get(modalID).props
            modalProps.active = Boolean(active)
            if (modalContainer) {
              modalContainer.style.display = modalProps.active ? null : 'none'
            }
          },
          mount() {
            const modalProps = modals.get(modalID).props
            modal.unmount()
            if (modalProps.active) {
              const modalDocument = modalProps.document || document
              // build modal
              ;[modalContainer, modalStyle, modalTarget] = buildModal(modalProps, modalDocument)
              // remove scrolling from body
              if (modalDocument?.body?.style) {
                backupOverflow = modalDocument.body.style.overflow
                modalDocument.body.style.overflow = 'hidden'
              }
              ViewportModule.root = modalContainer
              return modalTarget
            } else {
              ViewportModule.root = null
              return null
            }
          },
          unmount() {
            if (modalTarget) {
              modalTarget = null
            }
            if (modalContainer) {
              const modalProps = modals.get(modalID).props
              const modalDocument = modalProps.document || document
              modalDocument.body.removeChild(modalContainer)
              // restore scrolling to body
              if (modalDocument?.body?.style) {
                modalDocument.body.style.overflow = backupOverflow
                backupOverflow = ''
              }
              modalContainer = null
              ViewportModule.root = null
            }
          },
          get containerElement() {
            return modalContainer
          },
          get styleElement() {
            return modalStyle
          },
          get props() {
            return modals.get(modalID).props
          }
        }
        modals.set(modalID, { props: initProps, modal })
      }
    }
    return ModalModule
  },
  async load() {
    moduleLoaded = true
    return Promise.resolve(ModalModule)
  },
  get loaded() {
    return moduleLoaded
  },
  modal(modalID) {
    return modals.get(modalID)?.modal
  },
  findModalID(childNode) {
    for (const modalID of modals.keys()) {
      const modal = modals.get(modalID)
      if (modal.modal.containerElement?.contains(childNode)) {
        return modalID
      }
    }
    return null
  }
}

export default ModalModule

let modalIDCounter = 0
function buildModal(
  props: IModalProps,
  modalDocument: Document = document
): [HTMLDivElement, HTMLStyleElement, TReactRenderTarget] {
  modalIDCounter++
  const modalID = `${SWIDGET_MODAL_ID_PREFIX}${modalIDCounter}`
  const modalContainer = modalDocument.createElement('div')
  modalContainer.id = modalID
  modalContainer.classList.add(SWIDGET_MODAL_CLASS_NAME)

  const modalStyleEl = modalDocument.createElement('style')

  modalStyleEl.id = `${SWIDGET_MODAL_STYLE_ID_PREFIX}${modalIDCounter}`
  modalStyleEl.innerHTML = buildStyle(modalID, props)

  modalContainer.appendChild(modalStyleEl)

  const modalImageBackground = modalDocument.createElement('div')
  modalImageBackground.classList.add(SWIDGET_MODAL_IMAGE_BACKGROUND_CLASS_NAME)
  modalContainer.appendChild(modalImageBackground)

  const modalTarget = modalDocument.createElement('div')
  modalTarget.id = `${SWIDGET_MODAL_TARGET_ID_PREFIX}${modalIDCounter}`
  modalTarget.classList.add(SWIDGET_MODAL_TARGET_CLASS_NAME)
  modalContainer.appendChild(modalTarget)

  modalDocument.body.appendChild(modalContainer)

  return [modalContainer, modalStyleEl, modalTarget]
}

function buildStyle(modalID: string, props: IModalProps) {
  const modalStyle = `position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: ${props.zIndex};
    overflow: auto;
    transition: background-color ${SWIDGET_MODAL_BACKGROUND_TRANSITION} ease;
    background: transparent;`
  const modalTargetStyle = `margin-left: auto;
      margin-right: auto;`
  const backgroundColorStyle = props.background?.color ? `background-color: ${props.background.color};` : ''
  const backgroundImageStyle =
    props.background?.image && 'none' !== props.background.image
      ? `position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    opacity: 1;
    transition: opacity ${SWIDGET_MODAL_BACKGROUND_TRANSITION} ease;
    background-image: ${props.background.image};
    background-repeat: ${props.background?.tile ? 'repeat' : 'no-repeat'};
    background-position: ${props.background?.fit ? 'center center' : '0px 0px'};
    background-attachment: ${props.background?.fit ? 'fixed' : 'scroll'};
    -webkit-background-size: ${props.background?.fit ? 'cover' : 'auto'};
    -moz-background-size: ${props.background?.fit ? 'cover' : 'auto'};
    -o-background-size: ${props.background?.fit ? 'cover' : 'auto'};
    background-size: ${props.background?.fit ? 'cover' : 'auto'};`
      : ''
  const unhoveredBackgroundStyle = props.background?.unhovered ? `background-color: ${props.background.unhovered};` : ''

  return `
  #${modalID} {
    ${modalStyle}
    ${unhoveredBackgroundStyle ? unhoveredBackgroundStyle : backgroundColorStyle}
  }
  ${
    props.background?.image && 'none' !== props.background.image
      ? `#${modalID} .${SWIDGET_MODAL_IMAGE_BACKGROUND_CLASS_NAME} {
      ${backgroundImageStyle}
    }`
      : ''
  }
  ${
    unhoveredBackgroundStyle
      ? `#${modalID}:hover {
      ${backgroundColorStyle}
    }
    #${modalID} .${SWIDGET_MODAL_IMAGE_BACKGROUND_CLASS_NAME} {
      opacity: 0;
    }
    #${modalID}:hover .${SWIDGET_MODAL_IMAGE_BACKGROUND_CLASS_NAME} {
      opacity: 1;
    }
    #${modalID}:has(.${PREVIEW_TOOLBAR_CLASS_NAME}:hover) {
      ${unhoveredBackgroundStyle}
    }
    #${modalID}:has(.${PREVIEW_TOOLBAR_CLASS_NAME}:hover) .${SWIDGET_MODAL_IMAGE_BACKGROUND_CLASS_NAME} {
      opacity: 0;
    }`
      : ''
  }
  #${modalID} .${SWIDGET_MODAL_TARGET_CLASS_NAME} {
    ${modalTargetStyle}
  }
  `
}
